import type { CSSProperties, ReactNode } from 'react';
import { gaugeDash, pct1 } from '@piaip/shared';
import { sx, sxc } from '../sx';

/* Style strings carried over from the design, reused wherever it repeats them. */

export const CARD = 'background:#fff;border:1px solid #dbe3e0;border-radius:8px;padding:18px';
export const CARD_LIFT =
  'background:linear-gradient(180deg,#ffffff,#f6faf9);border:1px solid #dbe3e0;border-radius:12px;padding:18px;box-shadow:0 1px 0 #fff inset,0 20px 34px -24px rgba(13,59,53,.5),0 3px 6px -4px rgba(13,59,53,.2);min-width:0';
export const CARD_SHADOW =
  'background:#fff;border:1px solid #dbe3e0;border-radius:12px;padding:18px;box-shadow:0 1px 0 #fff inset,0 18px 32px -24px rgba(13,59,53,.45)';
export const TILE =
  'background:linear-gradient(180deg,#ffffff,#f4f8f7);border:1px solid #dbe3e0;border-radius:11px;padding:15px 16px;display:flex;flex-direction:column;gap:5px;box-shadow:0 1px 0 #fff inset,0 16px 28px -20px rgba(13,59,53,.5),0 2px 4px -3px rgba(13,59,53,.25)';

export const LABEL =
  'font-size:10.5px;letter-spacing:.08em;text-transform:uppercase;color:#5a6b68;font-weight:600';
export const SERIF_H = "font-family:'IBM Plex Serif',serif;font-size:16px;font-weight:600";
export const SERIF_H_LG = "font-family:'IBM Plex Serif',serif;font-size:20px;font-weight:600";
export const HINT = 'font-size:12px;color:#5a6b68';

export const INPUT =
  'padding:9px 10px;border:1px solid #d3dcda;border-radius:4px;font-size:14px;width:100%;box-sizing:border-box';
export const INPUT_CELL =
  'padding:8px;border:1px solid #d3dcda;border-radius:4px;font-size:14px;width:100%;box-sizing:border-box';
export const INPUT_NUM = INPUT_CELL + ';text-align:right;background:#fffdf3';
export const SELECT =
  'padding:9px;border:1px solid #d3dcda;border-radius:4px;font-size:13.5px;background:#fff;width:100%;box-sizing:border-box';
export const SELECT_CELL =
  'padding:8px;border:1px solid #d3dcda;border-radius:4px;font-size:13px;width:100%;background:#fff;box-sizing:border-box';

export const BTN_PRIMARY =
  'background:#0f5c52;color:#fff;border:0;padding:11px 16px;border-radius:4px;font-size:14px;font-weight:600;cursor:pointer';
export const BTN_SOFT =
  'background:#eef4f2;color:#0f5c52;border:1px solid #cfe0dc;padding:9px 14px;border-radius:4px;font-size:13px;font-weight:600;cursor:pointer';
export const BTN_DANGER =
  'background:none;border:1px solid #e3c9c5;color:#a3372c;padding:9px 14px;border-radius:4px;font-size:13px;cursor:pointer';
export const BTN_3D =
  'background:linear-gradient(180deg,#14766a,#0d4a42);color:#fff;border:0;padding:11px 17px;border-radius:7px;font-size:14px;font-weight:600;cursor:pointer;box-shadow:0 1px 0 rgba(255,255,255,.25) inset,0 10px 18px -10px rgba(13,59,53,.8)';
export const BTN_X =
  'background:none;border:0;color:#a3372c;cursor:pointer;font-size:18px;line-height:1';

export const TH =
  'text-align:left;font-size:10.5px;letter-spacing:.06em;text-transform:uppercase;color:#5a6b68;font-weight:600;padding:8px 10px;border-bottom:1px solid #dbe3e0';
export const TH_S =
  'text-align:left;font-size:10.5px;letter-spacing:.06em;text-transform:uppercase;color:#5a6b68;font-weight:600;padding:8px;border-bottom:1px solid #dbe3e0';
export const TH_S_R = TH_S + ';text-align:right';
export const TD = 'padding:9px 10px;border-bottom:1px solid #eef2f1;font-size:13px';
export const TD_CELL = 'padding:5px 8px;border-bottom:1px solid #eef2f1';

/* Print-sheet styles, in points, exactly as the report design specifies. */
export const P_TH =
  'text-align:left;padding:7px 9px;border:1px solid #dbe3e0;font-size:8.5pt;text-transform:uppercase;letter-spacing:.05em;color:#5a6b68';
export const P_TH_R = P_TH + ';text-align:right';
export const P_TD = 'padding:7px 9px;border:1px solid #dbe3e0';
export const P_TD_R = P_TD + ";text-align:right;font-family:'IBM Plex Mono',monospace";
export const P_TH_SM =
  'text-align:left;padding:6px 8px;border:1px solid #dbe3e0;font-size:8.5pt;text-transform:uppercase;color:#5a6b68';
export const P_TH_SM_R = P_TH_SM + ';text-align:right';
export const P_TD_SM = 'padding:6px 8px;border:1px solid #dbe3e0';
export const P_TD_SM_R = P_TD_SM + ";text-align:right;font-family:'IBM Plex Mono',monospace";
export const P_H = "font-family:'IBM Plex Serif',serif;font-size:13pt;font-weight:700;margin:0 0 8px";
export const P_TILE = 'border:1px solid #dbe3e0;padding:11px 12px;break-inside:avoid';

/** A stacked label-and-control pair, the design's standard form field. */
export function Field({
  label,
  htmlFor,
  children,
  style,
}: {
  label: string;
  htmlFor?: string;
  children: ReactNode;
  style?: string;
}) {
  return (
    <div style={sx(style ?? 'display:flex;flex-direction:column;gap:5px')}>
      <label htmlFor={htmlFor} style={sx(LABEL)}>
        {label}
      </label>
      {children}
    </div>
  );
}

export interface TileSpec {
  label: string;
  value: string;
  sub: string;
  color: string;
}

export function StatTile({ tile, className }: { tile: TileSpec; className?: string }) {
  return (
    <div className={className} style={sx(TILE)}>
      <div style={sx(LABEL)}>{tile.label}</div>
      <div
        style={sxc(
          "font-family:'IBM Plex Mono',monospace;font-size:25px;font-weight:500",
          `color:${tile.color}`,
        )}
      >
        {tile.value}
      </div>
      <div style={sx('font-size:12px;color:#5a6b68;line-height:1.4')}>{tile.sub}</div>
    </div>
  );
}

/** The rounded progress bar used throughout the dashboard. */
export function Bar({
  pct,
  color = '#0f5c52',
  height = 10,
  radius = 99,
  track = '#e7ecea',
  style,
}: {
  pct: number;
  color?: string;
  height?: number;
  radius?: number;
  track?: string;
  style?: CSSProperties;
}) {
  return (
    <div
      style={{
        height: `${height}px`,
        background: track,
        borderRadius: `${radius}px`,
        overflow: 'hidden',
        ...style,
      }}
    >
      <div
        style={{
          height: '100%',
          background: color,
          borderRadius: `${radius}px`,
          width: `${Math.max(0, Math.min(100, pct))}%`,
        }}
      />
    </div>
  );
}

/** One of the dashboard's gauge rings — work, money or time on the same scale. */
export function Dial({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div style={sx('display:flex;flex-direction:column;align-items:center;gap:7px')}>
      <div style={sx('position:relative;width:100%;max-width:128px')}>
        <svg
          viewBox="0 0 140 140"
          role="img"
          aria-label={`${label} ${pct1(value)}%`}
          style={sx('width:100%;height:auto;display:block;filter:drop-shadow(0 10px 12px rgba(13,59,53,.25))')}
        >
          <circle cx="70" cy="70" r="54" fill="none" stroke="#e7ecea" strokeWidth="15" />
          <circle
            cx="70"
            cy="70"
            r="54"
            fill="none"
            stroke={color}
            strokeWidth="15"
            strokeLinecap="butt"
            strokeDasharray={gaugeDash(value)}
            transform="rotate(-90 70 70)"
          />
        </svg>
        <div
          style={sx(
            'position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center',
          )}
        >
          <div style={sx("font-family:'IBM Plex Mono',monospace;font-size:20px;color:#0d3b35")}>
            {pct1(value)}%
          </div>
        </div>
      </div>
      <div style={sx('font-size:11.5px;color:#5a6b68;text-align:center')}>{label}</div>
    </div>
  );
}

/** An empty-state panel with the design's dashed outline. */
export function Empty({ children, pad = '36px 16px' }: { children: ReactNode; pad?: string }) {
  return (
    <div
      style={sx(
        `border:1px dashed #c3d0cd;border-radius:8px;padding:${pad};text-align:center;font-size:13px;color:#5a6b68`,
      )}
    >
      {children}
    </div>
  );
}

export function PageHead({
  title,
  note,
  action,
}: {
  title: string;
  note?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div style={sx('display:flex;justify-content:space-between;align-items:flex-end;gap:12px;flex-wrap:wrap')}>
      <div>
        <div style={sx(SERIF_H_LG)}>{title}</div>
        {note ? (
          <div style={sx('font-size:13px;color:#5a6b68;line-height:1.5;max-width:760px')}>{note}</div>
        ) : null}
      </div>
      {action}
    </div>
  );
}

